import { LoaderFunctionArgs } from "@remix-run/router/dist/utils"
import React from "react"
import { Col, Container, Form, Pagination, Row, Table } from "react-bootstrap"
import {
  Form as ReactForm,
  Link,
  redirect,
  useLoaderData,
  useNavigation,
  useSearchParams,
  useSubmit,
} from "react-router-dom"
import { pathList } from "../../routes/pathList"
import { ApiError, handleApiError } from "../../services/ApiService"
import shareService from "../../services/ShareService"
import { Share } from "../../services/ShareService/types"
import PaginationItems from "../../components/PatientFilesListing/PaginationItems"
import FileListModal from "./fileListModal"
import s from "./index.module.scss"
import { TableBody } from "./tableBody"
import { useUser } from "../../hooks"

// Must match ShareListPagination.page_size in portal/api/legacy/share/views.py
const PAGE_SIZE = 50

interface LoaderFunctionReturn {
  shares: Share[]
  count: number
  q: string
}

const List = () => {
  const { shares, count, q } = useLoaderData() as LoaderFunctionReturn
  const navigation = useNavigation()
  const submit = useSubmit()
  const [searchParams, setSearchParams] = useSearchParams()
  const searching =
    navigation.location &&
    new URLSearchParams(navigation.location.search).has("q")

  const requestedPage = Number(searchParams.get("page") ?? 1)
  const currentPage =
    Number.isFinite(requestedPage) && requestedPage > 0 ? requestedPage : 1
  const totalPages = Math.ceil(count / PAGE_SIZE)
  const normalizedPage = Math.min(currentPage, Math.max(1, totalPages))

  const [share, setShare] = React.useState<Share | undefined>()
  const handleShowClose = () => {
    setShare(undefined)
  }
  const showFiles = (share: Share) => {
    setShare(share)
  }
  const user = useUser()
  const adminUnits = user?.units.filter((u) => u.isAdmin) ?? []
  const hasAdminAccess = adminUnits.length > 0
  const hasMultipleUnits = (user?.units.length ?? 0) > 1

  const updatePage = (pageNumber: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("page", String(pageNumber))
      return next
    })
  }

  return (
    <>
      <Container>
        <Row className={s.heading}>
          <Col sm={11}>
            <h1>Shared files</h1>
          </Col>
          {hasAdminAccess && (
            <Col className="text-right">
              <Link
                to={pathList.create.reverse()}
                className="btn btn-outline-primary"
              >
                Create
              </Link>
            </Col>
          )}
        </Row>
        <Row>
          <Col>
            <ReactForm role="search">
              <Form.Group className="input-group mb-3">
                <Form.Control
                  id="q"
                  className={searching ? "loading" : ""}
                  aria-label="Search"
                  placeholder="Search by name, file name, MR Number or code"
                  type="search"
                  name="q"
                  defaultValue={q}
                  onChange={(e) => {
                    const isFirstSearch = q === null
                    submit(e.currentTarget.form, {
                      replace: !isFirstSearch,
                    })
                  }}
                />
                <input type="hidden" name="page" value="1" />
              </Form.Group>
              <div id="search-spinner" aria-hidden hidden={!searching} />
              <div className="sr-only" aria-live="polite"></div>
            </ReactForm>
          </Col>
        </Row>
        <Row>
          <Table bordered={true} striped={true} hover={true}>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">MR Number</th>
                <th scope="col">DOB</th>
                <th scope="col">Status</th>
                <th scope="col">Created</th>
                <th scope="col">Expires</th>
                <th scope="col">Downloaded</th>
                {hasMultipleUnits && <th scope="col">Unit</th>}
                <th scope="col">Files</th>
                <th scope="col">Share</th>
                {hasAdminAccess && <th scope="col"></th>}
              </tr>
            </thead>
            <tbody>
              <TableBody items={shares} showFiles={showFiles} />
            </tbody>
          </Table>
        </Row>
        {totalPages > 1 && (
          <Row className={s.paginationRow}>
            <Col xs={12} className="d-flex justify-content-end flex-wrap">
              <Pagination className="mb-0">
                <Pagination.Prev
                  aria-label="Go to previous page"
                  disabled={normalizedPage === 1}
                  onClick={() => updatePage(Math.max(normalizedPage - 1, 1))}
                >
                  Previous
                </Pagination.Prev>
                <PaginationItems
                  currentPage={normalizedPage}
                  totalPages={totalPages}
                  handlePageChange={updatePage}
                />
                <Pagination.Next
                  aria-label="Go to next page"
                  disabled={normalizedPage === totalPages}
                  onClick={() =>
                    updatePage(Math.min(normalizedPage + 1, totalPages))
                  }
                >
                  Next
                </Pagination.Next>
              </Pagination>
            </Col>
          </Row>
        )}
      </Container>
      {share !== undefined && (
        <FileListModal share={share} onClose={handleShowClose} />
      )}
    </>
  )
}

List.loader = async ({
  request,
}: LoaderFunctionArgs): Promise<LoaderFunctionReturn | Response> => {
  const url = new URL(request.url)
  const q = url.searchParams.get("q") ?? ""
  const page = Number(url.searchParams.get("page") ?? 1)
  const safePage = Number.isFinite(page) && page > 0 ? page : 1
  const result = await shareService.list(q, safePage, PAGE_SIZE)
  if (result instanceof ApiError) {
    return handleApiError(result)
  }

  const totalPages = Math.ceil(result.count / PAGE_SIZE)
  if (totalPages > 0 && safePage > totalPages) {
    const redirectUrl = new URL(url)
    redirectUrl.searchParams.set("page", String(totalPages))
    return redirect(redirectUrl.pathname + redirectUrl.search)
  }

  return {
    shares: result.results.map((share) => new Share(share)),
    count: result.count,
    q,
  }
}

export default List
